import { close, launchAsync } from '@microsoft/immersive-reader-sdk';
import { Content as ImmersiveReaderContent, Chunk as ImmersiveReaderChunk } from '@microsoft/immersive-reader-sdk/lib/content';
import { Options as ImmersiveReaderOptions } from '@microsoft/immersive-reader-sdk/lib/options';
import { Observable, throwError, from, defer, of, forkJoin, Subscriber, empty, isObservable } from 'rxjs';
import { flatMap, shareReplay, tap, merge, map, catchError, take } from 'rxjs/operators';
import { ImmersiveReaderConfig, ImmersiveReaderContentCallback } from '../interfaces/immersive-reader-config';
import { ImmersiveReaderValues } from '../interfaces/immersive-reader-values';
import { pdsUtils } from '../pds-utils';
import { pdsI18n } from '../pds-i18n';
import { CancelImmersiveReaderRequest } from '../models/cancel-immersive-reader-request';

const immersiveReaderZIndex = 10000;
const defaultSeparator = '\r\n\r\n';

function throwImmersiveReaderError(message: string): Observable<never> {
  // display the error message in the console
  console.warn(message);

  return throwError(new Error(message));
}

class AccessTokenCache {
  private expiresAt: Date;

  private get isExpired(): boolean {
    return !this.expiresAt || this.expiresAt < new Date();
  }

  private accessTokenRequest: Observable<string> | undefined;

  private accessTokenCachedRequest: Observable<string> | undefined;

  private subdomainRequest: Observable<string> | undefined;

  private subdomainCachedRequest: Observable<string> | undefined;

  get cacheIsReady(): boolean {
    return !!this.accessTokenRequest && !!this.subdomainRequest;
  }

  getAccessToken(): Observable<string> {
    if (!this.accessTokenRequest) {
      return throwImmersiveReaderError('The access token request has not been initialized.');
    }

    // if the token has expired or has not been requested before...
    if (this.isExpired || !this.accessTokenCachedRequest) {
      // share the results of the configured request with any future requests
      this.accessTokenCachedRequest = this.accessTokenRequest.pipe(shareReplay());
    }

    return this.accessTokenCachedRequest;
  }

  getSubdomain(): Observable<string> {
    if (!this.subdomainRequest) {
      return throwImmersiveReaderError('The subdomain request has not been initialized.');
    }

    // if the subdomain has not been requested before...
    if (!this.subdomainCachedRequest) {
      // share the results of the configured request with any future requests
      this.subdomainCachedRequest = this.subdomainRequest.pipe(shareReplay());
    }

    return this.subdomainCachedRequest;
  }

  // If the application is using angular, it can pass in an observable from an HttpClient request.
  // If the application is using jquery, it can pass in a function that will return a $.get() promise when called.
  initializeRequest(accessTokenRequest: (() => Observable<string> | Promise<string>), subdomainRequest: (() => Observable<string> | Promise<string>)): void {
    // if the request is a function that returns a promise, then convert that to an observable so that we're only working with observables
    let accessTokenObservable = defer(() => {
      let request = accessTokenRequest();

      if (isObservable(request)) {
        return request;
      }

      return from(request);
    });

    this.accessTokenRequest = accessTokenObservable
      .pipe(tap(() => {
        // this token will expire in an hour. To be safe, get a new one after 59 minutes
        this.expiresAt = new Date();
        this.expiresAt.setMinutes(this.expiresAt.getMinutes() + 59);
      }));

    let subdomainObservable = defer(() => {
      let request = subdomainRequest();

      if (isObservable(request)) {
        return request;
      } else if (typeof request === 'string') {
        return of(request);
      }

      return from(request);
    });

    this.subdomainRequest = subdomainObservable;
  }

  clearCache(): void {
    this.accessTokenCachedRequest = undefined;
    this.accessTokenRequest = undefined;
    this.expiresAt = new Date();
    this.subdomainCachedRequest = undefined;
    this.subdomainRequest = undefined;
  }
}

export const immersiveReaderAccessTokenCache = new AccessTokenCache();

export class ImmersiveReaderService {
  errorFiringElement: string | HTMLElement;
  // copy launchAsync to a property so it can be overwritten when testing
  private msLaunchAsync = launchAsync;

  private defaultContentSeparator = defaultSeparator;

  constructor (element: string | HTMLElement) {
    this.errorFiringElement = element;
  }

  start(config: ImmersiveReaderConfig): Observable<HTMLDivElement> {
    let irRequest = forkJoin(
      this.getValues(config),
      immersiveReaderAccessTokenCache.getAccessToken(),
      immersiveReaderAccessTokenCache.getSubdomain())
      .pipe(flatMap(([values, token, subdomain]: [ImmersiveReaderValues, string, string]) => {
        if (values == null) {
          return throwImmersiveReaderError('The Immersive Reader could not resolve the title, content, or access token.');
        }

        if (!token) {
          return throwImmersiveReaderError('The access token was not generated correctly.');
        }

        if (!subdomain) {
          return throwImmersiveReaderError('The subdomain was not retrieved correctly.');
        }

        let dataChunks: Array<ImmersiveReaderChunk> = values.content.map((c: string) => {
          return {
            content: c,
            lang: config.contentLanguage,
            mimeType: 'text/plain'
          };
        });

        let irContent: ImmersiveReaderContent = {
          title: values.title,
          chunks: dataChunks
        };

        // create an observable that we can use to complete the IR observable
        let closeSubscriber: Subscriber<HTMLDivElement>;
        let closeObservable = new Observable<HTMLDivElement>((subscriber: Subscriber<HTMLDivElement>) => {
          closeSubscriber = subscriber;
        });

        let options: ImmersiveReaderOptions = {
          uiZIndex: immersiveReaderZIndex,
          uiLang: config.uiLanguage,
          // pass the exit through to subscribers by completing the subscription
          onExit: () => closeSubscriber.complete()
        };

        return from(this.msLaunchAsync(
          token,
          subdomain,
          irContent,
          options))
          // merge the normal IR stream with the close observable's stream so we can send a 'completion' signal
          .pipe(merge(closeObservable));
      }))
      .pipe(catchError((error: Error | CancelImmersiveReaderRequest) => {
        // if this was a cancel error, don't display any messages or rethrow the error
        if (error instanceof CancelImmersiveReaderRequest) {
          // just close the immersive reader if it was opened
          close();

          // and immediately complete the observable
          return empty();
        }

        // otherwise this is a real error - pass it through
        return throwError(error);
      }))
      // unsubscribe after the immersive reader is opened
      .pipe(take<HTMLDivElement>(1))
      // if subscribed to again, return the same HTMLDiv as the first subscription
      .pipe(shareReplay());

    // start the Immersive Reader, rethrow any errors so that other subscribers can see and handle them
    irRequest.subscribe(
      undefined,
      (error: Error | CancelImmersiveReaderRequest) => {
        // close the immersive reader
        close();

        // show the generic error message
        this.displayPdsMessage();

        // rethrow the error so subscribers can do with it as they please.
        return throwError(error);
      });

    // return the observable for subsequent subscriptions
    return irRequest;
  }

  setDefaultSeparator(separator: string): void {
    this.defaultContentSeparator = separator;
  }

  displayPdsMessage(): void {
    let element: HTMLElement | undefined;

    if (typeof this.errorFiringElement === 'string') {
      element = document.querySelector(this.errorFiringElement) as HTMLElement;
    } else {
      element = this.errorFiringElement;
    }

    if (element != null) {
      pdsUtils.fireMessageEvent(element, pdsI18n.getText('pds|immersive-reader|Functionality not available because of invalid configuration.  Please contact your system administrator.'), 'ERROR');
    }
  }

  private getContent(config: ImmersiveReaderConfig): Observable<Array<string>> {
    let content: Array<string> = [];
    if (config.contentCallback) {
      let callback = config.contentCallback;
      // defer so the callback isn't called until subscription time
      return defer(() => {
        let results: Observable<Array<string> | string> | Promise<Array<string> | string> | Array<string> | string = callback();

        // transform the results into an observable, no matter what it came in as
        let observable = isObservable(results)
            ? results
            // promise or string values can be resolved to a promise then an observable
            : from(Promise.resolve(results));

        return observable
          // make the results of the async operation an array, no matter what was returned
          .pipe(map((result: string | string[]) => typeof result === 'string' ? [result] : result))
          // copy the config, overwriting the callback with null and the array value with the results
          .pipe(map((result: string[]) => Object.assign({}, config, { contentCallback: null, contentArray: result })))
          // recursively call this function with the new config so that the results get processed correctly
          .pipe(flatMap((config: ImmersiveReaderConfig) => this.getContent(config)));
      });
    } else if (config.contentArray) {
      content = config.contentArray;
    } else if (config.content) {
      content = [config.content];
    } else if (config.contentSelector) {
      let targetElements: Array<HTMLElement> = Array.from(document.querySelectorAll(config.contentSelector));

      if (targetElements == null || targetElements.length === 0) {
        return throwImmersiveReaderError(`The Immersive Reader could not find '${config.contentSelector}'.`);
      }

      content = targetElements.map((el: HTMLElement) => el.innerText);
    }

    // if a separator is configured and there are multiple chunks of content, make sure to separate each chunk
    let separator = config.contentSeparator || this.defaultContentSeparator;
    if (content.length > 1 && separator != null) {
      content = content
        // insert a separator after each content item
        .reduce((result: Array<string>, contentItem: string) => result.concat(contentItem, separator), [])
        // then lop of the last item, which is a separator
        .slice(0, -1);
    }

    if (!Array.isArray(content) || content.length === 0) {
      return throwImmersiveReaderError(`The Immersive Reader requires text content, an array of text content, or a content element selector.`);
    }

    return of(content);
  }

  private getTitle(config: ImmersiveReaderConfig): Observable<string> {
    if (config.titleCallback) {
      let callback = config.titleCallback;
      return defer(() => {
        let results: string | Observable<string> | Promise<string> = callback();

        let observable = isObservable(results)
          ? results
          // promise or string values can be resolved to a promise then an observable
          : from(Promise.resolve(results));

        return observable;
      });
    }

    if (config.title) {
      return of(config.title);
    }

    if (config.titleSelector) {
      let targetElement: HTMLElement | null = document.querySelector(config.titleSelector);

      if (targetElement == null) {
        return throwImmersiveReaderError(`The Immersive Reader could not find '${config.titleSelector}'.`);
      }

      return of(targetElement.innerText);
    }

    return throwImmersiveReaderError(`The Immersive Reader requires a title or a title element selector.`);
  }

  private getValues(config: ImmersiveReaderConfig): Observable<ImmersiveReaderValues | undefined> {
    if (!immersiveReaderAccessTokenCache.cacheIsReady) {
      return throwImmersiveReaderError(`The Immersive Reader requires a way to retrieve an access token.`);
    }

    let titleObservable = this.getTitle(config);
    let contentObservable = this.getContent(config);

    return forkJoin(titleObservable, contentObservable)
      .pipe(map(([title, content]: [string, string[]]) => {
        return { content, title };
      }));
  }
}
